import React, { useEffect, useMemo } from "react";
import { ConnectedProps, connect } from "react-redux";
import { MapContainer, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { ReducerState } from "../../types";
import { getOrganization } from "../../actions";
import { LatLngTuple } from "leaflet";
import { useWebServer } from "../../providers";
import { Spin } from "antd";
import "leaflet.fullscreen/dist/Control.FullScreen.css";
import "./index.css";
import MarkerManager from "./MarkerManager";
import MapExpand from "./MapExpand";
import MapDownloadControl from "./MapDownload";
import { useListDevicesQuery } from "../../actions/devicesInQuery";
import { useListTrapSitesQuery } from "../../actions/trapsiteInQuery";
import { MarkerData } from "../../types/components/map";
import { useAppAlerts } from "../../hooks/useAppAlerts";

const mapStateToProps = (state: ReducerState) => {
  const map_center = state.devices?.selected?.gps ||
    state.organizations?.map_center || { lat: 32.7361613, lon: -98.2829359 };
  return {
    uuid: state.organizations?.uuid || null,
    map_center: [map_center?.lat, map_center?.lon] as LatLngTuple,
  };
};

const connector = connect(mapStateToProps, {
  getOrganization,
});

type Props = ConnectedProps<typeof connector> & {
  // Extra props go here
};

const MapComponent: React.FC<Props> = ({
  map_center,
  uuid,
  getOrganization,
}) => {
  const { sendRequest, isLoading } = useWebServer();

  useEffect(() => {
    if (!isLoading && !uuid) {
      getOrganization(sendRequest);
    }
  }, [getOrganization, sendRequest, isLoading, uuid]);

  const { data: alerts } = useAppAlerts();

  const { data: trapSitesData, isLoading: isTrapSiteLoading } =
    useListTrapSitesQuery();
  const { data: devices } = useListDevicesQuery();

  const markersData = useMemo((): MarkerData[] => {
    if (isTrapSiteLoading || !devices?.items || !trapSitesData?.items)
      return [];

    // sum maintainAlert and DetectionAlert of each device
    const deviceAgg = devices.items.map((device) => {
      if (!alerts?.items || alerts?.items?.length <= 0) return {};

      const deviceAlerts = alerts?.items.filter(
        (a) => a.device === device.uuid,
      );
      const hasMaintenanceAlert = deviceAlerts.filter(
        (a) => a.severity === "warning" || a.severity === "danger",
      ).length;
      const hasDetectionAlert = deviceAlerts.filter(
        (a) => a.tag === "Detection",
      ).length;

      return {
        uuid: device.uuid || "",
        deviceName: device.display_name || "N/A",
        maintainCount: hasMaintenanceAlert,
        detectionCount: hasDetectionAlert,
        trapSite: device.trap_id,
      };
    });

    return trapSitesData.items.map((trapSite) => {
      //loop through deviceAgg get all the device that is in this trapsite and agg detectionCount and maintainCount
      let detectionCount = 0;
      let maintainCount = 0;
      let trapCount = 0;
      deviceAgg.forEach((device) => {
        if (device.trapSite === trapSite._id) {
          detectionCount += device?.detectionCount || 0;
          maintainCount += device?.maintainCount || 0;
          trapCount += 1;
        }
      });

      return {
        id: trapSite.display_name,
        position: [
          trapSite.gps?.lat || 0,
          trapSite.gps?.lon || 0,
        ] as LatLngTuple,
        warning: maintainCount > 0 || detectionCount > 0,
        detectionsCount: detectionCount,
        maintainCount: maintainCount,
        trapCount: trapCount,
      };
    });
  }, [devices, trapSitesData, alerts, isTrapSiteLoading]);

  return (
    <div className="map-container-big">
      {uuid && !isLoading ? (
        <MapContainer
          worldCopyJump={true}
          center={map_center}
          minZoom={3}
          maxZoom={18}
          zoom={13}
          scrollWheelZoom={true}
          style={{
            width: "100%",
            height: "100%",
          }}
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">Google Map</a> contributors'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MarkerManager map_center={map_center} markersData={markersData} />
          <MapExpand />
          <MapDownloadControl />
        </MapContainer>
      ) : (
        <Spin size="large" />
      )}
    </div>
  );
};

export default connector(MapComponent);
